/**
 * Campaign List Page
 *
 * Displays all campaigns for the user's account with:
 * - Search functionality
 * - Advanced filters (status, manager, date range, tags)
 * - Sorting capabilities
 * - Pagination
 * - Bulk operations (status change, delete, assign manager, apply tags)
 * - Actions: View details, Edit, Delete, Copy API key, Duplicate
 */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { campaignService } from '../../services/campaigns';
import { useFilters } from '../../hooks/useFilters';
import { useIsMobile } from '../../hooks';
import { useAuthStore } from '../../store/authStore';
import type { Campaign, CampaignStatus } from '../../types/campaign';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Alert } from '../../components/ui/Alert';
import { SkeletonCampaignList } from '../../components/ui';
import { SearchBar } from '../../components/campaigns/SearchBar';
import { FilterPanel } from '../../components/campaigns/FilterPanel';
import { QuickFilters } from '../../components/campaigns/QuickFilters';
import { TagBadge } from '../../components/campaigns/TagBadge';
import { DuplicateCampaignModal } from '../../components/campaigns/DuplicateCampaignModal';
import { BulkActionBar } from '../../components/campaigns/BulkActionBar';
import { BulkTagModal } from '../../components/campaigns/BulkTagModal';
import { BulkManagerModal } from '../../components/campaigns/BulkManagerModal';
import { CampaignMobileCard } from '../../components/campaigns/CampaignMobileCard';

function TrafficCell({ h24, total }: { h24: number; total: number }) {
  return <span className="text-sm font-mono text-gray-700">{h24} / {total}</span>;
}

function TrafficHeader({ label }: { label: string }) {
  const [show, setShow] = useState(false);
  return (
    <div className="flex items-center gap-1 relative">
      {label}
      <span
        className="cursor-help text-gray-400 hover:text-gray-600"
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
      >
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        {show && (
          <div className="absolute z-10 top-full left-1/2 -translate-x-1/2 mt-1 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap normal-case font-normal tracking-normal">
            24h / Total
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 border-4 border-transparent border-b-gray-900" />
          </div>
        )}
      </span>
    </div>
  );
}

export default function CampaignListPage() {
  const { t } = useTranslation('campaigns');
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { user } = useAuthStore();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [meta, setMeta] = useState<any>(null);

  // Check if user can create campaigns (has made payment)
  const canCreateCampaigns = user?.account?.can_use_platform ?? false;

  // Multi-select state
  const [selectedCampaigns, setSelectedCampaigns] = useState<number[]>([]);

  // Modal states
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [campaignToDuplicate, setCampaignToDuplicate] = useState<Campaign | null>(null);
  const [bulkTagModalOpen, setBulkTagModalOpen] = useState(false);
  const [bulkManagerModalOpen, setBulkManagerModalOpen] = useState(false);

  const {
    filters,
    setSearch,
    setStatus,
    setManagerId,
    setTagIds,
    setDateRange,
    setSort,
    setPage,
    resetFilters,
    hasActiveFilters,
  } = useFilters();

  // Fetch campaigns whenever filters change
  useEffect(() => {
    fetchCampaigns();
  }, [filters]);

  // Clear selection when campaigns change
  useEffect(() => {
    setSelectedCampaigns([]);
  }, [campaigns]);

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await campaignService.getCampaigns({
        search: filters.search,
        status: filters.status,
        managerId: filters.managerId,
        tagIds: filters.tagIds,
        createdFrom: filters.createdFrom,
        createdTo: filters.createdTo,
        sort: filters.sort,
        direction: filters.direction,
        page: filters.page,
        perPage: filters.perPage,
      });
      setCampaigns(data.campaigns);
      setMeta(data.meta);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  // Delete campaign
  const handleDelete = async (id: number, name: string) => {
    if (!confirm(t('messages.delete_confirm', { name }))) {
      return;
    }

    try {
      await campaignService.deleteCampaign(id);
      toast.success(t('messages.deleted_success'));
      fetchCampaigns();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('messages.delete_failed'));
    }
  };

  // Copy API key
  const handleCopyApiKey = (apiKey: string) => {
    navigator.clipboard.writeText(apiKey);
    toast.success(t('messages.api_key_copied'));
  };

  // Handle duplicate
  const handleDuplicate = (campaign: Campaign) => {
    setCampaignToDuplicate(campaign);
    setDuplicateModalOpen(true);
  };

  const handleDuplicateSuccess = (newCampaign: Campaign) => {
    fetchCampaigns();
    navigate(`/campaigns/${newCampaign.id}`);
  };

  // Multi-select handlers
  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedCampaigns(campaigns.map((c) => c.id));
    } else {
      setSelectedCampaigns([]);
    }
  };

  const handleSelectCampaign = (id: number) => {
    setSelectedCampaigns((prev) =>
      prev.includes(id) ? prev.filter((cid) => cid !== id) : [...prev, id]
    );
  };

  // Bulk operation handlers
  const handleBulkStatusChange = async (status: CampaignStatus) => {
    if (!confirm(t('bulk.status_change_confirm', { count: selectedCampaigns.length, status }))) {
      return;
    }

    try {
      await campaignService.bulkUpdateStatus(selectedCampaigns, status);
      toast.success(t('bulk.status_change_success', { count: selectedCampaigns.length, status }));
      setSelectedCampaigns([]);
      fetchCampaigns();
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('bulk.status_change_failed'));
    }
  };

  const handleBulkDelete = async () => {
    if (
      !confirm(
        t('bulk.delete_confirm', { count: selectedCampaigns.length })
      )
    ) {
      return;
    }

    try {
      await campaignService.bulkDelete(selectedCampaigns);
      toast.success(t('bulk.delete_success', { count: selectedCampaigns.length }));
      setSelectedCampaigns([]);
      fetchCampaigns();
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('bulk.delete_failed'));
    }
  };

  const handleBulkAssignManager = () => {
    setBulkManagerModalOpen(true);
  };

  const handleBulkManagerSuccess = () => {
    setSelectedCampaigns([]);
    fetchCampaigns();
  };

  const handleBulkApplyTags = () => {
    setBulkTagModalOpen(true);
  };

  const handleBulkTagSuccess = () => {
    fetchCampaigns();
    setSelectedCampaigns([]);
  };

  // Get status badge variant
  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'paused':
        return 'warning';
      case 'archived':
        return 'default';
      default:
        return 'default';
    }
  };

  // Handle sort
  const handleSort = (field: string) => {
    const newDirection =
      filters.sort === field && filters.direction === 'asc' ? 'desc' : 'asc';
    setSort(field, newDirection);
  };

  // Get sort icon
  const getSortIcon = (field: string) => {
    if (filters.sort !== field) {
      return (
        <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    return filters.direction === 'asc' ? (
      <svg className="w-4 h-4 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg className="w-4 h-4 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  if (loading && !campaigns.length) {
    return <SkeletonCampaignList />;
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900">{t('list.title')}</h1>
          <p className="mt-1 text-sm text-gray-500">
            {meta ? t('list.total_campaigns', { total: meta.total }) : t('list.subtitle')}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={() => navigate('/campaigns/tags')}
            className="btn btn-secondary w-full sm:w-auto"
          >
            <svg
              className="w-5 h-5 mr-2 inline-block"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
              />
            </svg>
            {t('list.manage_tags')}
          </button>
          {canCreateCampaigns && (
            <button
              onClick={() => navigate('/campaigns/new')}
              className="btn btn-primary w-full sm:w-auto"
            >
              {t('list.create_button')}
            </button>
          )}
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Payment Required Alert */}
      {!canCreateCampaigns && (
        <Alert variant="warning">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="font-semibold mb-1">{t('list.payment_required_title')}</h3>
              <p className="text-sm">{t('list.payment_required_message')}</p>
            </div>
            <button
              onClick={() => navigate('/subscription')}
              className="btn btn-primary ml-4 whitespace-nowrap"
            >
              {t('list.view_plans')}
            </button>
          </div>
        </Alert>
      )}

      {/* Search and Quick Filters */}
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
          <div className="flex-1">
            <SearchBar
              value={filters.search}
              onChange={setSearch}
              placeholder={t('list.search_placeholder')}
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`px-4 py-2 border rounded-md font-medium text-sm w-full sm:w-auto touch-manipulation ${
              showFilters || hasActiveFilters
                ? 'bg-indigo-50 border-indigo-300 text-indigo-700'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <div className="flex items-center justify-center sm:justify-start gap-2">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              {t('list.filters_button')}
              {hasActiveFilters && (
                <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-indigo-600 rounded-full">
                  {[
                    filters.search,
                    filters.status.length,
                    filters.managerId,
                    filters.createdFrom,
                    filters.createdTo,
                  ].filter(Boolean).length}
                </span>
              )}
            </div>
          </button>
        </div>

        {/* Quick Filters */}
        <QuickFilters
          onApply={(quickFilters) => {
            if (quickFilters.status) setStatus(quickFilters.status);
            if (quickFilters.managerId) setManagerId(quickFilters.managerId);
          }}
        />

        {/* Active Filter Chips */}
        {hasActiveFilters && (
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-sm text-gray-500">{t('list.active_filters')}</span>
            {filters.search && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-indigo-100 text-indigo-800">
                {t('list.filter_search', { search: filters.search })}
                <button
                  onClick={() => setSearch('')}
                  className="ml-2 hover:text-indigo-600"
                >
                  ×
                </button>
              </span>
            )}
            {filters.status.map((status) => (
              <span
                key={status}
                className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-indigo-100 text-indigo-800"
              >
                {t('list.filter_status', { status })}
                <button
                  onClick={() => setStatus(filters.status.filter((s) => s !== status))}
                  className="ml-2 hover:text-indigo-600"
                >
                  ×
                </button>
              </span>
            ))}
            {filters.managerId && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-indigo-100 text-indigo-800">
                {t('list.filter_manager', { managerId: filters.managerId })}
                <button
                  onClick={() => setManagerId(undefined)}
                  className="ml-2 hover:text-indigo-600"
                >
                  ×
                </button>
              </span>
            )}
            <button
              onClick={resetFilters}
              className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
            >
              {t('list.clear_all')}
            </button>
          </div>
        )}
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <FilterPanel
          status={filters.status}
          onStatusChange={setStatus}
          managerId={filters.managerId}
          onManagerChange={setManagerId}
          tagIds={filters.tagIds}
          onTagIdsChange={setTagIds}
          createdFrom={filters.createdFrom}
          createdTo={filters.createdTo}
          onDateRangeChange={setDateRange}
          onReset={resetFilters}
          hasActiveFilters={hasActiveFilters}
        />
      )}

      {/* Bulk Action Bar */}
      {selectedCampaigns.length > 0 && (
        <BulkActionBar
          selectedCount={selectedCampaigns.length}
          onStatusChange={handleBulkStatusChange}
          onAssignManager={handleBulkAssignManager}
          onApplyTags={handleBulkApplyTags}
          onDelete={handleBulkDelete}
          onClear={() => setSelectedCampaigns([])}
        />
      )}

      {/* Campaigns Table */}
      <Card>
        {campaigns.length === 0 ? (
          <div className="text-center py-12">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              {hasActiveFilters ? t('list.no_matches') : t('list.no_campaigns')}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {hasActiveFilters
                ? t('list.no_matches_description')
                : t('list.no_campaigns_description')}
            </p>
            {!hasActiveFilters && canCreateCampaigns && (
              <div className="mt-6">
                <button
                  onClick={() => navigate('/campaigns/new')}
                  className="btn btn-primary"
                >
                  {t('list.create_button')}
                </button>
              </div>
            )}
          </div>
        ) : (
          <>
            {/* Mobile View: Card Layout */}
            {isMobile ? (
              <div className="p-4 space-y-4">
                {/* Select All on Mobile */}
                <div className="flex items-center justify-between mb-4">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <input
                      type="checkbox"
                      checked={
                        selectedCampaigns.length === campaigns.length && campaigns.length > 0
                      }
                      onChange={handleSelectAll}
                      className="h-5 w-5 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    {t('list.select_all')}
                  </label>
                  <span className="text-sm text-gray-500">
                    {selectedCampaigns.length > 0 &&
                      t('list.selected_count', { count: selectedCampaigns.length })}
                  </span>
                </div>

                {/* Mobile Cards */}
                {campaigns.map((campaign) => (
                  <CampaignMobileCard
                    key={campaign.id}
                    campaign={campaign}
                    isSelected={selectedCampaigns.includes(campaign.id)}
                    onToggleSelect={handleSelectCampaign}
                    onClick={(c) => navigate(`/campaigns/${c.id}`)}
                    renderActions={(c) => (
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/statistics?campaignId=${c.id}`);
                          }}
                          className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                        >
                          {t('actions.statistics')}
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCopyApiKey(c.api_key);
                          }}
                          className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                        >
                          {t('actions.copy_key')}
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDuplicate(c);
                          }}
                          className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                        >
                          {t('actions.duplicate')}
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(c.id, c.name);
                          }}
                          className="text-sm text-red-600 hover:text-red-700 font-medium"
                        >
                          {t('actions.delete')}
                        </button>
                      </div>
                    )}
                  />
                ))}
              </div>
            ) : (
              /* Desktop View: Table Layout */
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left">
                        <input
                          type="checkbox"
                          checked={
                            selectedCampaigns.length === campaigns.length && campaigns.length > 0
                          }
                          onChange={handleSelectAll}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                      </th>
                      <th
                        onClick={() => handleSort('name')}
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      >
                        <div className="flex items-center gap-1">
                          {t('table.name')}
                          {getSortIcon('name')}
                        </div>
                      </th>
                      <th
                        onClick={() => handleSort('status')}
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      >
                        <div className="flex items-center gap-1">
                          {t('table.status')}
                          {getSortIcon('status')}
                        </div>
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Target URL
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Bot URL
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <TrafficHeader label="Target Traffic" />
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <TrafficHeader label="Bot Traffic" />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('table.tags')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('table.manager')}
                      </th>
                      <th
                        onClick={() => handleSort('created_at')}
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      >
                        <div className="flex items-center gap-1">
                          {t('table.created')}
                          {getSortIcon('created_at')}
                        </div>
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('table.actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {campaigns.map((campaign) => (
                      <tr key={campaign.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <input
                            type="checkbox"
                            checked={selectedCampaigns.includes(campaign.id)}
                            onChange={() => handleSelectCampaign(campaign.id)}
                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Link
                            to={`/campaigns/${campaign.id}`}
                            className="text-indigo-600 hover:text-indigo-900 font-medium"
                          >
                            {campaign.name}
                          </Link>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <Badge variant={getStatusVariant(campaign.status)}>
                            {campaign.status}
                          </Badge>
                        </td>
                        <td className="px-4 py-4">
                          <span className="text-xs text-gray-600 truncate block max-w-[140px]" title={campaign.human_redirect_url}>
                            {campaign.human_redirect_url}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <span className="text-xs text-gray-600 truncate block max-w-[140px]" title={campaign.bot_redirect_url}>
                            {campaign.bot_redirect_url}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <TrafficCell
                            h24={campaign.traffic_stats?.human_24h ?? 0}
                            total={campaign.traffic_stats?.human_total ?? 0}
                          />
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <TrafficCell
                            h24={campaign.traffic_stats?.bot_24h ?? 0}
                            total={campaign.traffic_stats?.bot_total ?? 0}
                          />
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex gap-1 overflow-x-auto max-w-xs">
                            {campaign.tags && campaign.tags.length > 0 ? (
                              campaign.tags.map((tag) => (
                                <TagBadge key={tag.id} tag={tag} />
                              ))
                            ) : (
                              <span className="text-xs text-gray-400">{t('table.no_tags')}</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {campaign.assigned_manager?.name || (
                            <span className="text-gray-400">{t('table.unassigned')}</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(campaign.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end gap-1">
                            {/* View Details */}
                            <button
                              onClick={() => navigate(`/campaigns/${campaign.id}`)}
                              title={t('actions.view_details')}
                              className="p-1.5 rounded-md text-indigo-400 hover:text-indigo-600 hover:bg-indigo-50 transition-all"
                            >
                              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            </button>

                            {/* View Statistics */}
                            <button
                              onClick={() => navigate(`/statistics?campaignId=${campaign.id}`)}
                              title={t('actions.view_statistics')}
                              className="p-1.5 rounded-md text-blue-400 hover:text-blue-600 hover:bg-blue-50 transition-all"
                            >
                              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                              </svg>
                            </button>

                            {/* Edit */}
                            <button
                              onClick={() => navigate(`/campaigns/${campaign.id}/edit`)}
                              title={t('actions.edit')}
                              className="p-1.5 rounded-md text-green-400 hover:text-green-600 hover:bg-green-50 transition-all"
                            >
                              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>

                            {/* Download Integration */}
                            <button
                              onClick={() => navigate(`/campaigns/${campaign.id}/integration`)}
                              title={t('actions.download_integration')}
                              className="p-1.5 rounded-md text-purple-400 hover:text-purple-600 hover:bg-purple-50 transition-all"
                            >
                              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                              </svg>
                            </button>

                            {/* Duplicate */}
                            <button
                              onClick={() => handleDuplicate(campaign)}
                              title={t('actions.duplicate')}
                              className="p-1.5 rounded-md text-orange-400 hover:text-orange-600 hover:bg-orange-50 transition-all"
                            >
                              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            </button>

                            {/* Delete */}
                            <button
                              onClick={() => handleDelete(campaign.id, campaign.name)}
                              title={t('actions.delete')}
                              className="p-1.5 rounded-md text-red-400 hover:text-red-600 hover:bg-red-50 transition-all"
                            >
                              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {meta && meta.last_page > 1 && (
              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  {t('pagination.showing', { from: meta.from, to: meta.to, total: meta.total })}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(filters.page - 1)}
                    disabled={filters.page === 1}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    {t('pagination.previous')}
                  </button>
                  <div className="flex gap-1">
                    {Array.from({ length: meta.last_page }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => setPage(page)}
                        className={`px-3 py-1 border rounded-md text-sm font-medium ${
                          filters.page === page
                            ? 'bg-indigo-600 text-white border-indigo-600'
                            : 'border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => setPage(filters.page + 1)}
                    disabled={filters.page === meta.last_page}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    {t('pagination.next')}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>

      {/* Duplicate Modal */}
      <DuplicateCampaignModal
        campaign={campaignToDuplicate}
        open={duplicateModalOpen}
        onClose={() => {
          setDuplicateModalOpen(false);
          setCampaignToDuplicate(null);
        }}
        onSuccess={handleDuplicateSuccess}
      />

      {/* Bulk Tag Modal */}
      <BulkTagModal
        campaignIds={selectedCampaigns}
        open={bulkTagModalOpen}
        onClose={() => setBulkTagModalOpen(false)}
        onSuccess={handleBulkTagSuccess}
      />

      {/* Bulk Manager Modal */}
      <BulkManagerModal
        campaignIds={selectedCampaigns}
        open={bulkManagerModalOpen}
        onClose={() => setBulkManagerModalOpen(false)}
        onSuccess={handleBulkManagerSuccess}
      />
    </div>
  );
}
